import { ErrorHandler } from '@angular/core';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';

const MAX_MESSAGE_LENGTH = 500;

/**
 * Anything thrown inside the app ends up here, and zone.js / rxjs happily rethrow values that
 * aren't errors (DOM events from IndexedDB or failed asset loads, plain objects, null/undefined).
 * Bugsnag can't build a report from those, so it substitutes an opaque `InvalidError` and the
 * original cause is lost. Convert those values into real Errors that describe what was thrown
 * before handing them over. Values Bugsnag already understands are passed through untouched.
 */
export function normaliseError(value: unknown): unknown {
    if (isUsableByBugsnag(value)) return value;

    const original = unwrapOriginalError(value);
    if (original !== value && isUsableByBugsnag(original)) return original;

    return buildError(value);
}

/** Mirrors the values `@bugsnag/core` can turn into a report without falling back to `InvalidError`. */
function isUsableByBugsnag(value: unknown): boolean {
    switch (typeof value) {
        case 'string':
        case 'number':
        case 'boolean':
            return true;
        case 'object':
            return value !== null && (isError(value) || hasErrorFields(value));
        default:
            return false;
    }
}

function isError(value: unknown): boolean {
    switch (Object.prototype.toString.call(value)) {
        case '[object Error]':
        case '[object Exception]':
        case '[object DOMException]':
            return true;
        default:
            return value instanceof Error;
    }
}

function hasErrorFields(value: object): boolean {
    const candidate = value as { name?: unknown; errorClass?: unknown; message?: unknown; errorMessage?: unknown };
    const hasName = typeof candidate.name === 'string' || typeof candidate.errorClass === 'string';
    const hasMessage = typeof candidate.message === 'string' || typeof candidate.errorMessage === 'string';
    return hasName && hasMessage;
}

/** Angular (DI, zone.js) wraps the thing that actually failed rather than rethrowing it. */
function unwrapOriginalError(value: unknown): unknown {
    if (value === null || typeof value !== 'object') return value;
    const wrapper = value as { ngOriginalError?: unknown; originalError?: unknown; rejection?: unknown };
    return wrapper.ngOriginalError ?? wrapper.originalError ?? wrapper.rejection ?? value;
}

function buildError(value: unknown): Error {
    const error = new Error(describeValue(value));
    error.name = errorClassFor(value);

    // Keep the original throw site when the value carried one, otherwise the only stack we can
    // offer is this handler's, which says nothing about where the failure came from.
    const stack = value !== null && typeof value === 'object' ? (value as { stack?: unknown }).stack : undefined;
    if (typeof stack === 'string' && stack !== '') error.stack = stack;

    return error;
}

function errorClassFor(value: unknown): string {
    if (value === null) return 'NonError(null)';
    if (value === undefined) return 'NonError(undefined)';

    const constructor = (value as { constructor?: unknown }).constructor;
    const name = typeof constructor === 'function' && typeof constructor.name === 'string' && constructor.name !== '' ? constructor.name : typeof value;
    return `NonError(${name})`;
}

function describeValue(value: unknown): string {
    if (value === null || value === undefined) return `Angular error handler received ${String(value)}`;
    if (typeof value === 'function') return `Angular error handler received function ${value.name || '(anonymous)'}`;
    if (typeof Event !== 'undefined' && value instanceof Event) return describeEvent(value);
    return truncate(safeStringify(value));
}

/** IndexedDB, XHR and asset loads report failures as events, with the useful detail on the target. */
function describeEvent(event: Event): string {
    const target = event.target as { error?: { name?: string; message?: string }; src?: string; url?: string } | null;

    const targetError = target?.error;
    if (targetError && (typeof targetError.name === 'string' || typeof targetError.message === 'string')) {
        return `${event.type} event: ${targetError.name ?? 'Error'}: ${targetError.message ?? ''}`.trim();
    }

    const message = (event as ErrorEvent).message;
    if (typeof message === 'string' && message !== '') return `${event.type} event: ${message}`;

    const source = target?.src ?? target?.url;
    if (typeof source === 'string' && source !== '') return `${event.type} event: ${source}`;

    return `${event.type} event`;
}

function safeStringify(value: unknown): string {
    const seen = new WeakSet<object>();
    try {
        const json = JSON.stringify(value, (_key, entry) => {
            if (typeof entry === 'object' && entry !== null) {
                if (seen.has(entry)) return '[Circular]';
                seen.add(entry);
            }
            if (typeof entry === 'function') return `[Function ${entry.name || 'anonymous'}]`;
            if (typeof entry === 'bigint') return String(entry);
            return entry;
        });
        return json ?? stringifyFallback(value);
    } catch {
        return stringifyFallback(value);
    }
}

function stringifyFallback(value: unknown): string {
    try {
        return String(value);
    } catch {
        return Object.prototype.toString.call(value);
    }
}

function truncate(message: string): string {
    return message.length > MAX_MESSAGE_LENGTH ? `${message.slice(0, MAX_MESSAGE_LENGTH)}...` : message;
}

/** `ErrorHandler` that normalises non-error values before passing them to Bugsnag. */
export class NormalisingErrorHandler implements ErrorHandler {
    constructor(private readonly delegate: ErrorHandler = new BugsnagErrorHandler()) {}

    handleError(error: unknown): void {
        let normalised: unknown = error;
        try {
            normalised = normaliseError(error);
        } catch {
            // Never let normalising swallow the report.
            normalised = error;
        }
        this.delegate.handleError(normalised);
    }
}
